// src/components/GameList.jsx
import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../../firebase';
import {
  getStorage,
  ref as storageRef,
  listAll,
  deleteObject
} from 'firebase/storage';

const ITEMS_PER_PAGE = 15;

export default function GameList() {
  const [games, setGames] = useState([]);
  const [popularIds, setPopularIds] = useState([]);
  const [soonIds, setSoonIds] = useState([]);
  const [gamejamIds, setGamejamIds] = useState([]); // 게임잼 배너 ID 목록
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      // 1) 모든 게임 불러오기
      const gamesSnap = await getDocs(collection(db, 'games'));
      setGames(gamesSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // 2) bannerList 문서에서 popular, soon, gamejam 배열 읽어오기
      const bannerRef = doc(db, 'data', 'bannerList');
      const bannerSnap = await getDoc(bannerRef);
      if (bannerSnap.exists()) {
        const data = bannerSnap.data();
        setPopularIds(Array.isArray(data.popular) ? data.popular : []);
        setSoonIds(Array.isArray(data.soon) ? data.soon : []);
        setGamejamIds(Array.isArray(data.gamejam) ? data.gamejam : []); // 추가
      }

      setPage(1);
    })();
  }, []);

  // Storage 폴더 및 하위 파일 재귀 삭제
  const deleteStorageFolder = async path => {
    const storage = getStorage();
    const folderRef = storageRef(storage, path);
    const { items, prefixes } = await listAll(folderRef);
    await Promise.all(items.map(i => deleteObject(i)));
    await Promise.all(prefixes.map(p => deleteStorageFolder(p.fullPath)));
  };

  // 게임 삭제
  const handleDelete = async id => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;
    try {
      await deleteStorageFolder(`games/${id}`);
      await deleteDoc(doc(db, 'games', id));
      setGames(g => g.filter(x => x.id !== id));
      setPopularIds(p => p.filter(x => x !== id));
      setSoonIds(s => s.filter(x => x !== id));
      setGamejamIds(gj => gj.filter(x => x !== id)); // 삭제 시 gamejam에서도 제거
      await updateDoc(doc(db, 'data', 'bannerList'), {
        popular: popularIds.filter(x => x !== id),
        soon: soonIds.filter(x => x !== id),
        gamejam: gamejamIds.filter(x => x !== id) // 업데이트
      });
    } catch (err) {
      console.error(err);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // 인기 배너 토글
  const handlePopularToggle = async (id, checked) => {
    let next = [...popularIds];
    if (checked) {
      if (next.length >= 4) {
        alert('인기배너는 최대 4개까지 선택할 수 있습니다.');
        return;
      }
      next.push(id);
    } else {
      next = next.filter(x => x !== id);
    }
    setPopularIds(next);
    await updateDoc(doc(db, 'data', 'bannerList'), { popular: next });
  };

  // 예정 배너 토글
  const handleSoonToggle = async (id, checked) => {
    const next = checked ? [...soonIds, id] : soonIds.filter(x => x !== id);
    setSoonIds(next);
    await updateDoc(doc(db, 'data', 'bannerList'), { soon: next });
  };

  // 게임잼 배너 토글
  const handleGamejamToggle = async (id, checked) => {
    let next = [...gamejamIds];
    if (checked) {
      next.push(id);
    } else {
      next = next.filter(x => x !== id);
    }
    setGamejamIds(next);
    await updateDoc(doc(db, 'data', 'bannerList'), { gamejam: next });
  };

  // 정렬 설정
  const handleSort = key => {
    setSortConfig(sc => {
      const direction = sc.key === key && sc.direction === 'asc' ? 'desc' : 'asc';
      return { key, direction };
    });
    setPage(1);
  };

  // 정렬된 게임 목록
  const sortedGames = useMemo(() => {
    const arr = [...games];
    const { key, direction } = sortConfig;
    if (!key) return arr;
    arr.sort((a, b) => {
      let aVal, bVal;
      if (key === 'popular') {
        aVal = popularIds.includes(a.id) ? 1 : 0;
        bVal = popularIds.includes(b.id) ? 1 : 0;
      } else if (key === 'soon') {
        aVal = soonIds.includes(a.id) ? 1 : 0;
        bVal = soonIds.includes(b.id) ? 1 : 0;
      } else if (key === 'gamejam') {
        aVal = gamejamIds.includes(a.id) ? 1 : 0;
        bVal = gamejamIds.includes(b.id) ? 1 : 0;
      } else if (key === 'title') {
        aVal = a.title.toLowerCase();
        bVal = b.title.toLowerCase();
        if (aVal < bVal) return direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return direction === 'asc' ? 1 : -1;
        return 0;
      } else if (key === 'status') {
        aVal = a.comingSoon ? 1 : 0;
        bVal = b.comingSoon ? 1 : 0;
      }
      return direction === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return arr;
  }, [games, popularIds, soonIds, gamejamIds, sortConfig]);

  const pageCount = Math.ceil(sortedGames.length / ITEMS_PER_PAGE);
  const startIdx = (page - 1) * ITEMS_PER_PAGE;
  const current = sortedGames.slice(startIdx, startIdx + ITEMS_PER_PAGE);

  const renderHeader = (label, key) => (
    <th className="text-center cursor-pointer select-none" onClick={() => handleSort(key)}>
      <div className="inline-flex items-center justify-center">
        <span>{label}</span>
        <span className="inline-block w-1 text-center">
          {sortConfig.key === key ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '\u00A0'}
        </span>
      </div>
    </th>
  );

  return (
    <div>
      <div className="flex justify-between items-center mt-4 mb-4">
        <h1 className="text-2xl font-bold">게임 관리 대시보드</h1>
        <button
          onClick={() => navigate('game/create')}
          className="px-4 py-2 border border-green-600 text-green-600 rounded hover:bg-green-50"
        >
          게임 등록
        </button>
      </div>

      <div className="overflow-auto bg-neutral-800 shadow rounded mb-4">
        <table className="min-w-full">
          <thead>
            <tr className="bg-neutral-700">
              {renderHeader('인기게임', 'popular')}
              {renderHeader('출시예정', 'soon')}
              {renderHeader('게임잼', 'gamejam')}
              {renderHeader('게임 이름', 'title')}
              <th className="p-3 text-center">고유 ID</th>
              {renderHeader('상태', 'status')}
              <th className="p-3 text-center">수정</th>
              <th className="p-3 text-center"></th>
            </tr>
          </thead>
          <tbody>
            {current.map(game => (
              <tr key={game.id} className="border-t">
                {/* 인기배너 체크박스 (패딩 축소) */}
                <td className="px-1 text-center">
                  <input
                    type="checkbox"
                    checked={popularIds.includes(game.id)}
                    onChange={e => handlePopularToggle(game.id, e.target.checked)}
                  />
                </td>

                {/* 예정배너 체크박스 (출시예정인 경우만 활성화) */}
                <td className="px-1 text-center">
                  <input
                    type="checkbox"
                    checked={soonIds.includes(game.id)}
                    disabled={!game.comingSoon}
                    onChange={e => handleSoonToggle(game.id, e.target.checked)}
                  />
                </td>

                {/* 게임잼배너 체크박스 (tags에 "게임잼" 포함된 경우만 활성화) */}
                <td className="px-1 text-center">
                  <input
                    type="checkbox"
                    checked={gamejamIds.includes(game.id)}
                    disabled={
                      !Array.isArray(game.tags) ||
                      !game.tags.some(t => t.includes('게임잼'))
                    }
                    onChange={e => handleGamejamToggle(game.id, e.target.checked)}
                  />
                </td>

                {/* 게임 이름 */}
                <td className="p-3 text-center">{game.title}</td>

                {/* 고유 ID */}
                <td className="p-3 text-center">{game.id}</td>

                {/* 상태: 출시 or 출시예정 */}
                <td className="p-3 text-center">
                  <span
                    className={`px-2 py-1 rounded text-xs font-medium ${
                      game.comingSoon ? 'bg-purple-600' : 'bg-green-500'
                    } text-white`}
                  >
                    {game.comingSoon ? '출시 예정' : '출시'}
                  </span>
                </td>

                {/* 수정 버튼들 */}
                <td className="p-3 text-center space-x-2">
                  <button
                    onClick={() => navigate(`/admin/game/edit/banner/${game.id}`)}
                    className="px-3 py-1 border border-blue-600 text-blue-600 rounded hover:bg-blue-50"
                  >
                    배너 수정
                  </button>
                  <button
                    onClick={() => navigate(`/admin/game/edit/detail/${game.id}`)}
                    className="px-3 py-1 border border-indigo-600 text-indigo-600 rounded hover:bg-indigo-50"
                  >
                    상점 편집
                  </button>
                </td>

                {/* 삭제 버튼 */}
                <td className="p-3 text-center">
                  <button
                    onClick={() => handleDelete(game.id)}
                    className="px-3 py-1 border border-red-600 text-red-600 rounded hover:bg-red-50"
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}

            {games.length === 0 && (
              <tr>
                <td colSpan="8" className="p-4 text-center text-gray-400">
                  표시할 게임이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex justify-center space-x-2">
        {Array.from({ length: pageCount }, (_, i) => i + 1).map(n => (
          <button
            key={n}
            onClick={() => setPage(n)}
            className={`px-3 py-1 rounded ${
              n === page ? 'bg-green-600 text-white' : 'bg-neutral-700 text-gray-200 hover:bg-neutral-600'
            }`}
          >
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}
